// First, import only the basic React components and types needed for context creation
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

// Now it's safe to import other components and hooks
import { replaceState } from 'history-throttled'
import { useLocation } from 'react-router-dom'

import { useLiquidityCheck, useQuote } from '@src/api/order.api'
// Import types and utilities that don't have dependencies on our components
import { ChainId } from '@src/configs/chainInfo'
import { getChainNameFromChainId } from '@src/configs/chainInfo'
import { DEFAULT_BUY_TOKEN, DEFAULT_SELL_TOKEN } from '@src/configs/defaults'
import { V2_API_URL } from '@src/configs/env'
import * as PATH from '@src/configs/paths'
import { QUOTE_REFRESH_BUFFER, QUOTE_REFRESH_INTERVAL } from '@src/constants/exchange'
import { LOCAL_STORAGE_BUY_TOKEN_KEY, LOCAL_STORAGE_SELL_TOKEN_KEY } from '@src/constants/localstorage'
import { ResultCode } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { MilesRequirementStatus, useMilesRequirement } from '@src/pages/Exchange/hooks/useMilesRequirement'
import { TransactionCapStatus, useTransactionCap } from '@src/pages/Exchange/hooks/useTransactionCap'
import { IExchangeData } from '@src/types/exchange'
import { IQuoteParams, IQuoteResponse } from '@src/types/order'
import { ISavedUrlParams } from '@src/types/savedUrlParams'
import { Address } from '@src/types/token'
import { isFeatherChain } from '@src/utils/chainUtils'
import { isFeatureEnabled } from '@src/utils/environment'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore, useTokensStore } from '@src/zustand_stores'

// Create the context before any potentially circular imports
// Define the type for the context data
interface ExchangeDataContextType {
    sellData: IExchangeData
    buyData: IExchangeData
    destinationAddress: Address | undefined
    quoteResponse: IQuoteResponse
    isQuoteLoading: boolean
    refetchQuote: () => Promise<any>
    invalidateQuoteAndRefetch: (newParams: IQuoteParams) => Promise<any>
    forceCancelQuote: () => Promise<any>
    mainWalletAddress: Address | undefined
    updateSellData: (updates: Partial<IExchangeData>) => void
    updateBuyData: (updates: Partial<IExchangeData>) => void
    updateBothData: (sellUpdates: Partial<IExchangeData>, buyUpdates: Partial<IExchangeData>) => void
    setDestinationAddress: (address: Address | undefined) => void
    transactionCapStatus: TransactionCapStatus | null
    isLoadingTransactionCap: boolean
    transactionCapError: string | null
    milesStatus: MilesRequirementStatus
    isFeatherOrder: boolean
    liquidityAvailable: boolean
    isCheckingLiquidity: boolean
    liquidityError: string | null
}

// Create the context with undefined as default value
const ExchangeDataContext = createContext<ExchangeDataContextType | undefined>(undefined)

// Create logger early
const logger = createContextLogger('ExchangeDataContext')

// Define default data
const DEFAULT_SELL_DATA: IExchangeData = {
    token: DEFAULT_SELL_TOKEN,
    chainId: DEFAULT_SELL_TOKEN.chainId,
    value: BigInt(0),
}

const DEFAULT_BUY_DATA: IExchangeData = {
    token: DEFAULT_BUY_TOKEN,
    chainId: DEFAULT_BUY_TOKEN.chainId,
    value: BigInt(0),
}

/**
 * Helper to load a token from localStorage and match it against the tokens store.
 */
function loadTokenFromLocalStorage(key: string, allTokens: IExchangeData['token'][]): IExchangeData['token'] | null {
    const saved = localStorage.getItem(key)
    // logger.info(`Locally saved token for key ${key}:`, saved)
    if (!saved) return null
    try {
        const tokenData = JSON.parse(saved)
        // Attempt to find a matching token in the current token list
        const found = allTokens.find(
            t => t.chainId === tokenData.chainId && t.address.toLowerCase() === tokenData.address.toLowerCase()
        )
        return found || null
    } catch {
        return null
    }
}

// Utility function to get URL parameters
function getURLParameters(): ISavedUrlParams {
    const queryString = window.location.search.substring(1)
    const parameterPairs = queryString.split('&')
    const parameters: ISavedUrlParams = {
        sellChain: '',
        buyChain: '',
        sellToken: '',
        buyToken: '',
    }

    for (const pair of parameterPairs) {
        const [key, value] = pair.split('=').map(decodeURIComponent)
        if (key && key in parameters) {
            parameters[key as keyof ISavedUrlParams] = value || ''
        }
    }

    return parameters as ISavedUrlParams
}

function getUpdatedURLString(obj: any): string {
    const parameters = getURLParameters()

    Object.keys(obj).forEach(key => {
        parameters[key as keyof ISavedUrlParams] = obj[key]
    })

    const queryStringComponents: string[] = []
    for (const key in parameters) {
        if (key && parameters[key as keyof ISavedUrlParams]) {
            queryStringComponents.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(parameters[key as keyof ISavedUrlParams])}`
            )
        }
    }

    const newQueryString = queryStringComponents.join('&')
    return '?' + newQueryString
}

export const useExchangeDataContext = (): ExchangeDataContextType => {
    const context = useContext(ExchangeDataContext)
    if (!context) {
        throw new Error('useExchangeDataContext must be used within an ExchangeDataProvider')
    }
    return context
}

interface ExchangeDataProviderProps {
    children: React.ReactElement
}

export const ExchangeDataProvider: React.FC<ExchangeDataProviderProps> = ({ children }) => {
    const location = useLocation()
    const { isQuoteEnabled, setIsQuoteEnabled } = useExchangeState()
    const { tokens, isLoading: tokensLoading } = useTokensStore()

    const walletSelectedSellToken = useApplicationStore(state => state.walletSelectedSellToken)
    const setWalletButtonDisplayChainId = useApplicationStore(state => state.setWalletButtonDisplayChainId)

    const [sellData, setSellData] = useState<IExchangeData>(DEFAULT_SELL_DATA)
    const [buyData, setBuyData] = useState<IExchangeData>(DEFAULT_BUY_DATA)

    const [destinationAddress, setDestinationAddress] = useState<Address | undefined>(undefined)

    const [quoteResponse, setIQuoteResponse] = useState<IQuoteResponse>({
        status: ResultCode.pending,
        quote: null,
    })

    const [isQuoteLoading, setIsQuoteLoading] = useState<boolean>(false)
    const [isReadyForQuote, setIsReadyForQuote] = useState(false)

    const { tronWalletAddress, solanaWalletAddress, evmWalletAddress } = useWalletConnections()

    // Determine main wallet address based on chain
    const mainWalletAddress = useMemo(() => {
        return sellData.chainId === ChainId.Solana
            ? solanaWalletAddress
            : ((sellData.chainId === ChainId.Tron ? tronWalletAddress : evmWalletAddress) as Address)
    }, [evmWalletAddress, sellData.chainId, solanaWalletAddress, tronWalletAddress])

    // Check if the buy chain is a Feather chain
    const isFeatherOrder = useMemo(() => isFeatherChain(buyData.chainId), [buyData.chainId])

    // Check if liquidity is available for this token pair
    const {
        data: liquidityData,
        isLoading: isCheckingLiquidity,
        error: liquidityCheckError,
    } = useLiquidityCheck(sellData.chainId, sellData.token?.address, buyData.chainId, buyData.token?.address, {
        // Only check liquidity when we have all the required data
        enabled: Boolean(
            sellData.token && buyData.token && !isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData)
        ),
    })

    // Determine if liquidity is available
    const liquidityAvailable = useMemo(() => {
        // If we're not using V2 API or checking liquidity, assume it's available
        if (!isFeatureEnabled('V2_MIGRATION') || !V2_API_URL) {
            logger.debug('V2 API not enabled, assuming liquidity is available')
            return true
        }

        // If we don't have liquidity data yet, don't make a determination
        if (!liquidityData && isCheckingLiquidity) {
            logger.debug('Still checking liquidity, no data yet')
            return true
        }

        // If we have liquidity data, use it
        if (liquidityData) {
            logger.debug('Liquidity check result:', {
                supported: liquidityData.supported,
                message: liquidityData.message,
            })
            return liquidityData.supported
        }

        // Default to available if we can't determine
        return true
    }, [liquidityData, isCheckingLiquidity])

    // Format liquidity error message if any
    const liquidityError = useMemo(() => {
        if (liquidityCheckError) {
            const errorMsg =
                liquidityCheckError instanceof Error ? liquidityCheckError.message : String(liquidityCheckError)
            logger.error('Liquidity check error:', { error: errorMsg })
            return errorMsg
        }

        if (liquidityData && !liquidityData.supported) {
            const errorMsg = liquidityData.message || 'Liquidity not available for this pair'
            logger.debug('Liquidity unavailable message:', { message: errorMsg })
            return errorMsg
        }

        return null
    }, [liquidityData, liquidityCheckError])

    const {
        data: useIQuoteResponse,
        isLoading: isUseQuoteLoading,
        isFetching: isQuoteFetching,
        refetch: refetchQuote,
        forceCancel: forceCancelQuote,
        invalidateQuoteAndRefetch,
    } = useQuote(
        {
            walletAddress: mainWalletAddress?.toString() || '',
            srcChain: getChainNameFromChainId(sellData.chainId),
            dstChain: getChainNameFromChainId(buyData.chainId),
            srcAssetAddress: sellData.token?.address || '',
            dstAssetAddress: buyData.token?.address || '',
            srcAmount: sellData.value,
            targetAddress: destinationAddress ?? '',
        },
        isQuoteEnabled && !isFeatherOrder && liquidityAvailable && !isCheckingLiquidity, // Only enable quote when liquidity is available and not still checking
        {
            refetchInterval: QUOTE_REFRESH_INTERVAL,
            staleTime: QUOTE_REFRESH_INTERVAL - QUOTE_REFRESH_BUFFER,
            enabled:
                isReadyForQuote &&
                !isWrapOnly(sellData, buyData) &&
                !isUnWrapOnly(sellData, buyData) &&
                Boolean(sellData.value && sellData.token && buyData.token) &&
                !isFeatherOrder && // Skip regular quote for Feather orders
                !isCheckingLiquidity && // Don't fetch quotes while checking liquidity
                liquidityAvailable, // Only fetch quotes if liquidity is available
        }
    )

    const { transactionCapStatus, isLoadingTransactionCap, transactionCapError } = useTransactionCap({
        buyData,
        isQuoteLoading,
        mainWalletAddress,
        sellData,
    })

    const milesStatus = useMilesRequirement({
        buyData,
        sellData,
        mainWalletAddress,
    })

    // Effect to handle liquidity check results and update buy data value
    useEffect(() => {
        // If checking liquidity, don't modify the buy data yet
        if (isCheckingLiquidity) {
            return
        }

        // If liquidity is unavailable, set buy data value to 0
        if (!liquidityAvailable && !isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData)) {
            // Only update if the value isn't already 0
            if (buyData.value !== BigInt(0)) {
                setBuyData(prev => ({ ...prev, value: BigInt(0) }))
            }
        }
    }, [isCheckingLiquidity, liquidityAvailable, sellData, buyData])

    useEffect(() => {
        // Only update loading state from useQuote if not a Feather order
        // For Feather orders, the loading state is managed by the Feather-specific code
        if (!isFeatherOrder) {
            setIsQuoteLoading(isUseQuoteLoading || isQuoteFetching)
            if (!isUseQuoteLoading && !isQuoteFetching) {
                setIQuoteResponse(useIQuoteResponse)
            }
        } else {
            // For Feather orders, we need to explicitly clear the isQuoteLoading state
            // This ensures it doesn't interfere with the Feather-specific loading state
            setIsQuoteLoading(false)
        }
    }, [useIQuoteResponse, isUseQuoteLoading, isQuoteFetching, isFeatherOrder])

    useEffect(() => {
        setWalletButtonDisplayChainId(sellData?.chainId)
    }, [sellData?.chainId])

    useEffect(() => {
        // walletSelectedSellToken only changes when someone clicks an asset in the wallet drawer.
        if (walletSelectedSellToken) {
            // updateSellData internally handles changing URL and updating localStorage
            updateSellData({ token: walletSelectedSellToken, chainId: walletSelectedSellToken.chainId })
        }
    }, [walletSelectedSellToken])

    /**
     * Reads tokens from the URL first; if absent or invalid, tries localStorage;
     * then finally falls back to default tokens.
     */
    useEffect(() => {
        // Only run when the user is on the SWAP page and tokens are loaded
        if (location.pathname === PATH.SWAP && !tokensLoading) {
            const params = getURLParameters()

            // SELL token
            let sellFoundToken: IExchangeData['token'] | null = null

            // First check if we have a current token that's not the default
            if (sellData.token && sellData.token !== DEFAULT_SELL_TOKEN) {
                sellFoundToken = sellData.token
                // logger.info('Using current sell token:', sellFoundToken.symbol)
            }

            // Then check URL params if no current token
            if (!sellFoundToken && params.sellChain && params.sellToken) {
                try {
                    const foundToken = tokens.find(
                        o =>
                            o.chainId === Number(params.sellChain) &&
                            o.address.toLowerCase() === params.sellToken.toLowerCase()
                    )
                    if (foundToken) {
                        sellFoundToken = foundToken
                        logger.info('sellData updated from URL params:', sellFoundToken.symbol)
                    } else {
                        logger.debug(
                            `cannot find token with chainId=${Number(params.sellChain)} and address=${params.sellToken}`
                        )
                    }
                } catch (e) {
                    logger.error('Cannot parse sellData from URL parameters:', e)
                }
            }

            // If no valid token from URL, try localStorage
            if (!sellFoundToken) {
                const localStoredSellToken = loadTokenFromLocalStorage(LOCAL_STORAGE_SELL_TOKEN_KEY, tokens)
                if (localStoredSellToken) {
                    sellFoundToken = localStoredSellToken
                    logger.info('sellData updated from localStorage:', localStoredSellToken.symbol)
                } else {
                    logger.info('No sellData found in localStorage.')
                }
            }

            // Only use default if we have no other token
            if (!sellFoundToken) {
                sellFoundToken = DEFAULT_SELL_TOKEN
                logger.info('sellData defaulted to:', DEFAULT_SELL_TOKEN.symbol)
            }

            // BUY token
            let buyFoundToken: IExchangeData['token'] | null = null

            // First check if we have a current token that's not the default
            if (buyData.token && buyData.token !== DEFAULT_BUY_TOKEN) {
                buyFoundToken = buyData.token
                // logger.info('Using current buy token:', buyFoundToken.symbol)
            }

            // Then check URL params if no current token
            if (!buyFoundToken && params.buyChain && params.buyToken) {
                try {
                    const foundToken = tokens.find(
                        o =>
                            o.chainId === Number(params.buyChain) &&
                            o.address.toLowerCase() === params.buyToken.toLowerCase()
                    )
                    if (foundToken) {
                        buyFoundToken = foundToken
                        logger.info('buyData updated from URL params:', buyFoundToken.symbol)
                    } else {
                        logger.debug(
                            `cannot find token with chainId=${Number(params.buyChain)} and address=${params.buyToken}`
                        )
                    }
                } catch (e) {
                    logger.error('Cannot parse buyData from URL parameters:', e)
                }
            }

            // If no valid token from URL, try localStorage
            if (!buyFoundToken) {
                const localStoredBuyToken = loadTokenFromLocalStorage(LOCAL_STORAGE_BUY_TOKEN_KEY, tokens)
                if (localStoredBuyToken) {
                    buyFoundToken = localStoredBuyToken
                    logger.info('buyData updated from localStorage:', localStoredBuyToken.symbol)
                } else {
                    logger.info('No buyData found in localStorage.')
                }
            }

            // Only use default if we have no other token
            if (!buyFoundToken) {
                buyFoundToken = DEFAULT_BUY_TOKEN
                logger.info('buyData defaulted to:', DEFAULT_BUY_TOKEN.symbol)
            }

            // Check if buy token is the same as sell token (same token on same chain)
            if (
                sellFoundToken &&
                buyFoundToken &&
                sellFoundToken.chainId === buyFoundToken.chainId &&
                sellFoundToken.address.toLowerCase() === buyFoundToken.address.toLowerCase()
            ) {
                logger.warn('Buy token is the same as sell token, using default buy token instead')

                // Try to find a different token on the same chain first
                const differentTokenOnSameChain = tokens.find(
                    t =>
                        t.chainId === buyFoundToken!.chainId &&
                        t.address.toLowerCase() !== sellFoundToken!.address.toLowerCase()
                )

                if (differentTokenOnSameChain) {
                    buyFoundToken = differentTokenOnSameChain
                    logger.info('Using different token on same chain:', buyFoundToken.symbol)
                } else {
                    // Fall back to default buy token if no other token found on same chain
                    buyFoundToken = DEFAULT_BUY_TOKEN
                    logger.info('No alternative token found on chain, using default:', DEFAULT_BUY_TOKEN.symbol)
                }
            }

            // Only update if either token has changed
            const sellTokenChanged = sellFoundToken !== sellData.token
            const buyTokenChanged = buyFoundToken !== buyData.token

            if (sellTokenChanged || buyTokenChanged) {
                updateBothData(
                    {
                        token: sellFoundToken,
                        chainId: sellFoundToken.chainId,
                    },
                    {
                        token: buyFoundToken,
                        chainId: buyFoundToken.chainId,
                    }
                )
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, tokensLoading, tokens])

    const updateSellData = (updates: Partial<IExchangeData>) => {
        setIsReadyForQuote(false)

        let stateUpdate: any = {}
        if (
            updates.token &&
            (updates.token.address.toLowerCase() !== sellData.token.address.toLowerCase() ||
                updates.token.chainId !== sellData.token.chainId)
        ) {
            stateUpdate = { ...stateUpdate, sellChain: updates.token.chainId, sellToken: updates.token.address }
        }
        const urlString = getUpdatedURLString(stateUpdate)
        replaceState(null, '', urlString)

        setSellData(prevSellData => ({
            ...prevSellData,
            ...updates,
        }))

        setTimeout(() => {
            setIsReadyForQuote(true)
        }, 10)
    }

    const updateBuyData = (updates: Partial<IExchangeData>) => {
        setIsReadyForQuote(false)

        let stateUpdate: any = {}
        if (
            updates.token &&
            (updates.token.address.toLowerCase() !== buyData.token.address.toLowerCase() ||
                updates.token.chainId !== buyData.token.chainId)
        ) {
            stateUpdate = { ...stateUpdate, buyChain: updates.token.chainId, buyToken: updates.token.address }
        }
        const urlString = getUpdatedURLString(stateUpdate)
        replaceState(null, '', urlString)

        setBuyData(prevBuyData => ({
            ...prevBuyData,
            ...updates,
        }))

        setTimeout(() => {
            setIsReadyForQuote(true)
        }, 10)
    }

    const updateBothData = (sellUpdates: Partial<IExchangeData>, buyUpdates: Partial<IExchangeData>) => {
        setIsReadyForQuote(false)

        let stateUpdate: any = {}
        if (
            sellUpdates.token &&
            (sellUpdates.token.address.toLowerCase() !== sellData.token.address.toLowerCase() ||
                sellUpdates.token.chainId !== sellData.token.chainId)
        ) {
            stateUpdate = {
                ...stateUpdate,
                sellChain: sellUpdates.token.chainId,
                sellToken: sellUpdates.token.address,
            }
        }
        if (
            buyUpdates.token &&
            (buyUpdates.token.address.toLowerCase() !== buyData.token.address.toLowerCase() ||
                buyUpdates.token.chainId !== buyData.token.chainId)
        ) {
            stateUpdate = {
                ...stateUpdate,
                buyChain: buyUpdates.token.chainId,
                buyToken: buyUpdates.token.address,
            }
        }
        const urlString = getUpdatedURLString(stateUpdate)
        replaceState(null, '', urlString)

        setSellData(prev => ({ ...prev, ...sellUpdates }))
        setBuyData(prev => ({ ...prev, ...buyUpdates }))

        setTimeout(() => {
            setIsReadyForQuote(true)
        }, 10)
    }

    useEffect(() => {
        logger.info('sellData in provider:', sellData)
    }, [sellData])

    useEffect(() => {
        logger.info('buyData in provider:', buyData)
    }, [buyData])

    // FE-523, FE-643
    useEffect(() => {
        if (isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData)) {
            const reason = isWrapOnly(sellData, buyData) ? 'wrap only' : 'unwrap only'
            logger.warn(`[ExchangeDataContext] Quote disabled: ${reason} operation`)
            // Disable quote loading and the quote API call for wrap/unwrap operations
            setIsQuoteLoading(false)
            setIsQuoteEnabled(false)
        } else {
            // Re-enable quotes for normal token exchanges
            setIsQuoteEnabled(true)
        }
    }, [sellData.token, buyData.token, setIsQuoteEnabled])

    const quoteHookEnabled = isQuoteEnabled && !isFeatherOrder && liquidityAvailable && !isCheckingLiquidity
    useEffect(() => {
        if (isQuoteEnabled && !quoteHookEnabled) {
            const reasons: string[] = []
            if (isFeatherOrder) reasons.push('Feather order')
            if (!liquidityAvailable) reasons.push('liquidity unavailable')
            if (isCheckingLiquidity) reasons.push('checking liquidity')
            logger.warn(`[ExchangeDataContext] useQuote disabled due to: ${reasons.join(', ')}`)
        }
    }, [isQuoteEnabled, isFeatherOrder, liquidityAvailable, isCheckingLiquidity])

    /**
     * Whenever sellData.token changes, store it in localStorage.
     */
    useEffect(() => {
        if (sellData?.token) {
            localStorage.setItem(LOCAL_STORAGE_SELL_TOKEN_KEY, JSON.stringify(sellData.token))
            logger.info(`sellData (${sellData.token.symbol}) saved to localStorage.`)
        }
    }, [sellData.token])

    /**
     * Whenever buyData.token changes, store it in localStorage.
     */
    useEffect(() => {
        if (buyData?.token) {
            localStorage.setItem(LOCAL_STORAGE_BUY_TOKEN_KEY, JSON.stringify(buyData.token))
            logger.info(`buyData (${buyData.token.symbol}) saved to localStorage.`)
        }
    }, [buyData.token])

    const contextValue = {
        sellData,
        buyData,
        destinationAddress,
        quoteResponse,
        isQuoteLoading,
        refetchQuote,
        invalidateQuoteAndRefetch,
        forceCancelQuote,
        mainWalletAddress,
        updateSellData,
        updateBuyData,
        updateBothData,
        setDestinationAddress,
        transactionCapStatus,
        isLoadingTransactionCap,
        transactionCapError,
        isFeatherOrder,
        liquidityAvailable,
        isCheckingLiquidity,
        liquidityError,
        milesStatus,
    }

    return <ExchangeDataContext.Provider value={contextValue}>{children}</ExchangeDataContext.Provider>
}
